import {Component, EventEmitter, Input, OnInit, Output, ViewChild, ChangeDetectionStrategy} from '@angular/core';
import {ShadeToolsService} from '../../../core/services/shade-tools.service';
import {getFriendlyTimeZoneString, Hub, HubId, HubTimeZone} from '../../../shared/models/hub';
import {HubsService} from '../../../core/services/hubs.service';
import {Store} from '@ngrx/store';
import {AppState} from '../../../app/app.state';
import {map, take} from 'rxjs/operators';
import {SaveRevertButtonsComponent, SaveRevertButtonsStatus} from '../save-revert-buttons/save-revert-buttons.component';
import {DrawerHeightFromBottomComponent} from '../drawer-height-from-bottom/drawer-height-from-bottom.component';
import {SpinnerComponent} from '../spinner/spinner.component';
import {FormsModule} from '@angular/forms';
import {PillComponent} from '../pill/pill.component';
import {FeatureFlag, FeatureFlagsService} from '@core/services/feature-flags.service';

@Component({
  selector: 'motn-hub-editor',
  templateUrl: './hub-editor.component.html',
  styleUrls: ['./hub-editor.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    DrawerHeightFromBottomComponent,
    SpinnerComponent,
    SaveRevertButtonsComponent,
    FormsModule,
    PillComponent,
  ],
})
export class HubEditorComponent implements OnInit {
  @Input() hubId: HubId;
  @Output() saved$: EventEmitter<Hub> = new EventEmitter<Hub>();
  @Output() clickedOutside$: EventEmitter<null> = new EventEmitter<null>();

  hub: Hub;

  @ViewChild('saveRevertButtonsComponent') buttons: SaveRevertButtonsComponent;
  status: SaveRevertButtonsStatus;

  @ViewChild('lorawanSaveButtonsComponent') lorawanButtons: SaveRevertButtonsComponent;
  lorawanStatus: SaveRevertButtonsStatus;

  gatewayEuis: string[] = [];
  gatewayEuiInput: string = '';

  timeZones: HubTimeZone[] = [
    'America/New_York',
    'America/Chicago',
    'America/Denver',
    'America/Phoenix',
    'America/Los_Angeles',
    'America/Anchorage',
    'Pacific/Honolulu',
  ];


  get showGatewayEuis(): boolean {
    return this.featureFlagsService.isFeatureEnabled(FeatureFlag.ENABLE_MANAGE_GATEWAY_EUIS);
  }


  constructor(
    private featureFlagsService: FeatureFlagsService,
    private hubsService: HubsService,
    private shadeTools: ShadeToolsService,
    private store: Store<AppState>,
  ) {
  }


  ngOnInit(): void {
    this.hubsService.fetchLorawanGateways$(this.hubId).subscribe(
      (gateways) => {
        this.gatewayEuis = gateways.map(gateway => gateway.EUI);
      },
    );

    this.store.select('hubs').pipe(
      take(1),
    ).subscribe(
      (state) => {
        const hub: Hub = state.hubs[this.hubId];

        if (hub) {
          this.hub = JSON.parse(JSON.stringify(hub));
        } else {
          this.hubsService.fetchUserHubs$().pipe(
            map((hubs) => {
              return hubs.find(h => h.ID === this.hubId);
            }),
          ).subscribe(
            (hub) => {
              this.hub = hub;
            },
          );
        }
      },
    );
  }


  save() {
    this.hubsService.saveChangesToApi$(this.hub).subscribe(
      () => {
        this.buttons.showSavedStatus();
        this.saved$.next(this.hub);
      },
      () => {
        this.buttons.resetStatus();
        this.shadeTools.errorDialog();
      },
    );
  }


  updateStatus(status: SaveRevertButtonsStatus) {
    this.status = status;
  }


  addEui() {
    const euiRegex = /^[0-9A-Fa-f]{16}$/;
    const eui = this.gatewayEuiInput.trim();
    this.gatewayEuiInput = '';

    if (!eui || this.gatewayEuis.includes(eui) || !euiRegex.test(eui)) {
      return;
    }

    this.gatewayEuis = [...this.gatewayEuis, eui];
    this.updateLorawanStatus('edited');
  }


  removeEui(eui: string) {
    this.gatewayEuis = this.gatewayEuis.filter(e => e !== eui);
    this.updateLorawanStatus('edited');
  }


  saveGateways() {
    this.hubsService.saveLorawanGateways$(this.hubId, this.gatewayEuis).subscribe(
      () => {
        this.lorawanButtons.showSavedStatus();
      },
      () => {
        this.lorawanButtons.resetStatus();
        this.shadeTools.errorDialog();
      },
    );
  }


  updateLorawanStatus(status: SaveRevertButtonsStatus) {
    this.lorawanStatus = status;
  }


  getTimeZoneString(tz: HubTimeZone): string {
    return getFriendlyTimeZoneString(tz);
  }
}
